import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  createCustomFieldSchema,
  updateCustomFieldSchema,
} from "@/api/lookups";
import {
  CUSTOM_FIELD_TYPES,
  type CustomFieldEntityType,
  type CustomFieldSchemaResponse,
  type CustomFieldType,
} from "@/api/types";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CustomFieldFlags } from "@/components/registries/CustomFieldFlags";
import {
  customFieldFormSchema,
  defaultCustomFieldFormValues,
  type CustomFieldFormValues,
} from "@/components/registries/customFieldFormSchema";

export interface CustomFieldFormProps {
  open: boolean;
  existing: CustomFieldSchemaResponse | null;
  defaultEntity: CustomFieldEntityType;
  onClose: () => void;
  afterSubmit: () => Promise<unknown> | void;
}

export function CustomFieldForm({
  open,
  existing,
  defaultEntity,
  onClose,
  afterSubmit,
}: CustomFieldFormProps) {
  const { t } = useTranslation();
  const form = useForm<CustomFieldFormValues>({
    resolver: zodResolver(customFieldFormSchema),
    defaultValues: defaultCustomFieldFormValues(defaultEntity),
  });

  useEffect(() => {
    if (!open) return;
    if (existing === null) {
      form.reset(defaultCustomFieldFormValues(defaultEntity));
      return;
    }
    form.reset({
      entity_type: existing.entity_type,
      key: existing.key,
      label: existing.label,
      field_type: existing.field_type as CustomFieldType,
      is_required: existing.is_required,
      is_filterable: existing.is_filterable,
      order: existing.order,
    });
  }, [open, existing, defaultEntity, form]);

  const mutation = useMutation({
    mutationFn: async (v: CustomFieldFormValues) => {
      if (existing === null) {
        return createCustomFieldSchema({
          entity_type: v.entity_type,
          key: v.key,
          label: v.label,
          field_type: v.field_type,
          is_required: v.is_required ?? false,
          is_filterable: v.is_filterable ?? true,
          order: v.order ?? 0,
        });
      }
      return updateCustomFieldSchema(existing.id, {
        label: v.label,
        is_required: v.is_required,
        is_filterable: v.is_filterable,
        order: v.order,
      });
    },
    onSuccess: async () => {
      await afterSubmit();
      onClose();
    },
  });

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? undefined : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {existing === null
              ? t("adminCustomFields.createButton")
              : t("common.edit")}
          </DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={form.handleSubmit((v) => mutation.mutate(v))}
          data-testid="cf-form"
        >
          <div>
            <Label htmlFor="cf-entity">
              {t("adminCustomFields.fields.entityType")}
            </Label>
            <select
              id="cf-entity"
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              {...form.register("entity_type")}
              disabled={existing !== null}
            >
              <option value="client">
                {t("adminCustomFields.entity.client")}
              </option>
              <option value="employee">
                {t("adminCustomFields.entity.employee")}
              </option>
            </select>
          </div>
          <div>
            <Label htmlFor="cf-key">{t("adminCustomFields.fields.key")}</Label>
            <Input
              id="cf-key"
              {...form.register("key")}
              readOnly={existing !== null}
            />
            {form.formState.errors.key !== undefined ? (
              <p className="text-xs text-destructive">
                {t("adminCustomFields.errors.keyPattern")}
              </p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="cf-label">
              {t("adminCustomFields.fields.label")}
            </Label>
            <Input id="cf-label" {...form.register("label")} />
            {form.formState.errors.label !== undefined ? (
              <p className="text-xs text-destructive">
                {t("adminCustomFields.errors.labelRequired")}
              </p>
            ) : null}
          </div>
          <div>
            <Label htmlFor="cf-type">
              {t("adminCustomFields.fields.fieldType")}
            </Label>
            <select
              id="cf-type"
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              {...form.register("field_type")}
              disabled={existing !== null}
            >
              {CUSTOM_FIELD_TYPES.map((tp) => (
                <option key={tp} value={tp}>
                  {tp}
                </option>
              ))}
            </select>
          </div>
          <CustomFieldFlags
            requiredProps={form.register("is_required")}
            filterableProps={form.register("is_filterable")}
            orderProps={form.register("order", { valueAsNumber: true })}
          />
          {mutation.isError ? (
            <p className="text-xs text-destructive" role="alert">
              {t("adminCustomFields.errors.saveFailed")}
            </p>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? t("common.saving") : t("common.save")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
